// src/paginas/publico/login/login.tsx
import React, { useEffect, useState, forwardRef, ForwardedRef } from 'react';
import {
    Button,
    Container,
    IconButton,
    InputAdornment,
    Paper,
    TextField,
    Typography,
    Box,
    useTheme,
    alpha,
    Stack,
    CircularProgress
} from '@mui/material';
import { Visibility, VisibilityOff, LoginOutlined, CheckCircleOutline } from '@mui/icons-material';
import { useNavigate, useParams, useSearchParams } from 'react-router';

import ServiceAPI from '../../services/serviceapi';
import tokenService from '../../services/tokenservice';
import { SecUtils } from '../../utils/secutils';
import { RESPONSE } from '../../enums/response';

const CORES = {
    verde: '#82b07d',
    marinho: '#1d2b4b',
    fundo: '#f8fafc'
};

const Login = forwardRef((_props: unknown, _ref: ForwardedRef<any>) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { empresaParam } = useParams();
    const [searchParams] = useSearchParams();

    // Estados
    const [empresaID, setEmpresaID] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [senha, setSenha] = useState<string>('');
    const [erro, setErro] = useState<string>('');
    const [mostrarSenha, setMostrarSenha] = useState<boolean>(false);
    const [carregando, setCarregando] = useState<boolean>(false);
    const [checandoSessao, setChecandoSessao] = useState<boolean>(true);

    useEffect(() => {
        const inicializar = async () => {
            const tkn = await tokenService.getToken();
            if (tkn) {
                navigate("/gestao");
                return;
            }

            const idDaUrl = empresaParam || searchParams.get('xs');
            if (idDaUrl) {
                setEmpresaID(idDaUrl.toLowerCase().trim());
            }

            setChecandoSessao(false);
        };

        inicializar();
    }, [empresaParam, searchParams, navigate]);

    const podeLogar = () => {
        const emailValido = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
        return emailValido && empresaID.length >= 3 && senha.length >= 3 && !carregando;
    };

    const handleLogin = async () => {
        setCarregando(true);
        setErro('');

        try {
            const payload = {
                "e": SecUtils.encode(email),
                "p": SecUtils.encode(senha)
            };

            const response = await ServiceAPI.logar(empresaID, payload);

            if (response.codeResponse === RESPONSE.SUCESSO_LOGIN) {
                const tkn = response.dataResponse;
                tkn.empresaID = empresaID;
                await tokenService.saveToken(tkn);
                navigate("/gestao");
            } else {
                setErro("Credenciais inválidas para esta unidade.");
            }
        } catch (error: any) {
            const msg = error.response?.data?.message || error.message || "Erro ao realizar login";
            setErro(msg);
        } finally {
            setCarregando(false);
        }
    };

    const onKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && podeLogar()) handleLogin();
    };

    if (checandoSessao) {
        return (
            <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: CORES.fundo }}>
                <CircularProgress size={30} sx={{ color: CORES.verde }} />
            </Box>
        );
    }

    return (
        <Box 
            sx={{ 
                minHeight: '100vh', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                p: { xs: 2, sm: 0 },
                bgcolor: CORES.fundo,
                position: 'relative',
                overflow: 'hidden'
            }}
        >
            {/* Detalhe decorativo de fundo similar à Home */}
            <Box sx={{ 
                position: 'absolute', bottom: -100, left: -100, width: 400, height: 400, 
                borderRadius: '50%', bgcolor: 'rgba(130, 176, 125, 0.08)', zIndex: 0 
            }} />

            <Container maxWidth="xs" sx={{ p: 0, position: 'relative', zIndex: 1 }}>
                <Paper 
                    elevation={0}
                    sx={{ 
                        p: { xs: 4, sm: 6 },
                        borderRadius: '32px',
                        border: '1px solid #e2e8f0',
                        textAlign: 'center',
                        boxShadow: '0 20px 25px -5px rgba(0,0,0,0.05)'
                    }}
                >
                    <Box sx={{ mb: 5 }}>
                        <Box 
                            component="img" 
                            src="/AppCodesh.png" 
                            alt="App Codesh" 
                            sx={{ height: 40, mb: 3, cursor: 'pointer' }} 
                            onClick={() => navigate('/')}
                        />
                        <Typography variant="h5" fontWeight={900} sx={{ color: CORES.marinho, letterSpacing: '-0.02em' }}>
                            Bem-vindo de volta
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b', mt: 1 }}>
                            Acesse o painel de gestão da sua unidade
                        </Typography>
                    </Box>

                    <Stack spacing={2.5}>
                        <TextField
                            fullWidth
                            label="ID da Empresa"
                            variant="outlined"
                            autoComplete="off"
                            value={empresaID}
                            onChange={(e) => setEmpresaID(e.target.value.toLowerCase().replace(/\s/g, ''))}
                            sx={{ 
                                '& .MuiOutlinedInput-root': { 
                                    borderRadius: '14px',
                                    '&.Mui-focused fieldset': { borderColor: CORES.verde }
                                },
                                '& .MuiInputLabel-root.Mui-focused': { color: CORES.verde }
                            }}
                        />

                        <TextField
                            fullWidth
                            label="E-mail de acesso"
                            type="email"
                            variant="outlined"
                            autoComplete="username"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            sx={{ 
                                '& .MuiOutlinedInput-root': { 
                                    borderRadius: '14px',
                                    '&.Mui-focused fieldset': { borderColor: CORES.verde }
                                },
                                '& .MuiInputLabel-root.Mui-focused': { color: CORES.verde }
                            }}
                        />

                        <TextField
                            fullWidth
                            label="Senha"
                            type={mostrarSenha ? 'text' : 'password'}
                            variant="outlined"
                            autoComplete="current-password"
                            value={senha}
                            onChange={(e) => setSenha(e.target.value)}
                            onKeyDown={onKeyDown}
                            error={!!erro}
                            helperText={erro}
                            sx={{ 
                                '& .MuiOutlinedInput-root': { 
                                    borderRadius: '14px',
                                    '&.Mui-focused fieldset': { borderColor: !!erro ? theme.palette.error.main : CORES.verde }
                                },
                                '& .MuiInputLabel-root.Mui-focused': { color: !!erro ? theme.palette.error.main : CORES.verde }
                            }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setMostrarSenha(!mostrarSenha)} edge="end">
                                            {mostrarSenha ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />

                        <Button
                            variant="contained"
                            fullWidth
                            size="large"
                            onClick={handleLogin}
                            disabled={!podeLogar()}
                            disableElevation
                            sx={{ 
                                py: 2, 
                                borderRadius: '14px', 
                                fontWeight: 800, 
                                textTransform: 'none',
                                fontSize: '1rem',
                                bgcolor: CORES.verde,
                                '&:hover': { bgcolor: '#6d9668' },
                                mt: 1,
                                boxShadow: '0 10px 15px -3px rgba(130, 176, 125, 0.25)'
                            }}
                        >
                            {carregando ? <CircularProgress size={24} color="inherit" /> : 'Entrar no Sistema'}
                        </Button>
                    </Stack>

                    <Box sx={{ mt: 5 }}>
                        <Typography variant="caption" sx={{ color: '#94a3b8', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                            <CheckCircleOutline sx={{ fontSize: 14 }} /> Sistema Seguro & Autenticado
                        </Typography>
                    </Box>
                </Paper>

                <Box sx={{ mt: 3, textAlign: 'center' }}>
                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                        Ainda não tem uma conta? {' '}
                        <span 
                            onClick={() => navigate('/cadastro')}
                            style={{ color: CORES.verde, fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' }}
                        >
                            Experimente grátis
                        </span>
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
});

Login.displayName = 'Login';
export default Login;